import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import PageHeader from "../Components/PageHeader";
import Table from "../Components/Table";
import TableContent from "../Components/TableContent";
import TableHeader from "../Components/TableHeader";
import { AppContext } from "../services/context";
import offlineService from "../services/offlineService";
import request from "../services/request";
import endPoint from "../services/endPoint";

const OfflineBackup = () => {
  const authCtx = useContext(AppContext);
  const { user } = authCtx;
  
  const [databases, setDatabases] = useState([]);
  const [classes, setClasses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [selectedClasse, setSelectedClasse] = useState("");
  const [version, setVersion] = useState("");
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    loadData();
  }, [refresh]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadDatabases(),
        loadStats(),
        loadClasses(),
      ]);
    } catch (error) {
      console.error("Erreur lors du chargement des données:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadDatabases = async () => {
    try {
      const res = await offlineService.getAll();
      setDatabases(res.data.data || []);
    } catch (error) {
      console.error("Erreur lors du chargement des bases:", error);
      toast.error("Erreur lors du chargement des bases offline");
    }
  };

  const loadStats = async () => {
    try {
      const res = await offlineService.getStats();
      setStats(res.data.data || null);
    } catch (error) {
      console.error("Erreur lors du chargement des stats:", error);
    }
  };

  const loadClasses = async () => {
    try {
      const res = await request.get(endPoint.classes);
      console.log(res.data.data);
      setClasses(res.data.data || []);
    } catch (error) {
      console.error("Erreur lors du chargement des classes:", error);
    }
  };

  const handleGenerate = async () => {
    if (!selectedClasse) {
      toast.warning("Veuillez sélectionner une classe");
      return;
    }

    setGenerating(true);
    toast.promise(
      offlineService.generate(selectedClasse, version || null),
      {
        pending: "Génération en cours...",
        success: {
          render({ data }) {
            setRefresh(refresh + 1);
            setSelectedClasse("");
            setVersion("");
            return data.data.message || "Base générée avec succès";
          },
        },
        error: {
          render({ data }) {
            return data.response?.data?.message || "Erreur lors de la génération";
          },
        },
      }
    ).finally(() => setGenerating(false));
  };

  const handleGenerateAll = async () => {
    if (!window.confirm("Voulez-vous vraiment générer les bases offline pour toutes les classes ? Cette opération peut prendre du temps.")) {
      return;
    }

    setGeneratingAll(true);
    toast.promise(
      offlineService.generateAll(version || null),
      {
        pending: "Génération de toutes les bases en cours...",
        success: {
          render({ data }) {
            setRefresh(refresh + 1);
            const summary = data.data.summary;
            return `Génération terminée: ${summary.success} succès, ${summary.failed} échecs`;
          },
        },
        error: {
          render({ data }) {
            return data.response?.data?.message || "Erreur lors de la génération";
          },
        },
      }
    ).finally(() => setGeneratingAll(false));
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Voulez-vous vraiment supprimer la base "${filename}" ?`)) {
      return;
    }

    toast.promise(
      offlineService.delete(filename),
      {
        pending: "Suppression en cours...",
        success: {
          render({ data }) {
            setRefresh(refresh + 1);
            return data.data.message || "Base supprimée avec succès";
          },
        },
        error: {
          render({ data }) {
            return data.response?.data?.message || "Erreur lors de la suppression";
          },
        },
      }
    );
  };

  const handleDownload = (filename) => {
    const url = offlineService.getDownloadUrl(filename);
    const token = user.token;
    
    // Créer un lien temporaire avec le token pour le téléchargement
    fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.blob())
      .then((blob) => {
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      })
      .catch((error) => {
        console.error("Erreur de téléchargement:", error);
        toast.error("Erreur lors du téléchargement");
      });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
      <PageHeader title="Gestion des sauvegardes Offline" />

      {/* Section Statistiques */}
      {stats && (
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card bg-primary text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="card-subtitle mb-2">Total Classes</h6>
                    <h3 className="card-title mb-0">{stats.total_classes}</h3>
                  </div>
                  <i className="bi bi-building fs-1 opacity-50"></i>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-success text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="card-subtitle mb-2">Bases Générées</h6>
                    <h3 className="card-title mb-0">{stats.total_databases}</h3>
                  </div>
                  <i className="bi bi-database-fill fs-1 opacity-50"></i>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-info text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="card-subtitle mb-2">Taille Totale</h6>
                    <h3 className="card-title mb-0">{stats.total_size_formatted}</h3>
                  </div>
                  <i className="bi bi-hdd-fill fs-1 opacity-50"></i>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-warning text-dark">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="card-subtitle mb-2">Couverture</h6>
                    <h3 className="card-title mb-0">{stats.coverage_percentage}%</h3>
                  </div>
                  <i className="bi bi-pie-chart-fill fs-1 opacity-50"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Section Génération */}
      <div className="card mb-4">
        <div className="card-header bg-white">
          <h5 className="card-title mb-0">
            <i className="bi bi-plus-circle me-2"></i>
            Générer une base Offline
          </h5>
        </div>
        <div className="card-body">
          <div className="row align-items-end">
            <div className="col-md-4">
              <label className="form-label">Classe</label>
              <select
                className="form-select"
                value={selectedClasse}
                onChange={(e) => setSelectedClasse(e.target.value)}
                disabled={generating || generatingAll}
              >
                <option value="">-- Sélectionner une classe --</option>
                {classes.map((classe) => (
                  <option key={classe.id} value={classe.id}>
                    {classe.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Version (optionnel)</label>
              <input
                type="text"
                className="form-control"
                placeholder="Ex: v1.0.0"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                disabled={generating || generatingAll}
              />
            </div>
            <div className="col-md-5">
              <div className="d-flex gap-2">
                <button
                  className="btn btn-primary"
                  onClick={handleGenerate}
                  disabled={generating || generatingAll || !selectedClasse}
                >
                  {generating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Génération...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-gear me-2"></i>
                      Générer
                    </>
                  )}
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={handleGenerateAll}
                  disabled={generating || generatingAll}
                >
                  {generatingAll ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Génération...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-collection me-2"></i>
                      Générer toutes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Classes sans base offline */}
      {stats?.classes_without_offline?.length > 0 && (
        <div className="alert alert-warning mb-4">
          <h6 className="alert-heading">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Classes sans base offline ({stats.classes_without_offline.length})
          </h6>
          <div className="d-flex flex-wrap gap-2 mt-2">
            {stats.classes_without_offline.map((classe) => (
              <span key={classe.id} className="badge bg-warning text-dark">
                {classe.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Liste des bases */}
      <div className="card">
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">
            <i className="bi bi-database me-2"></i>
            Bases Offline Disponibles
          </h5>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setRefresh(refresh + 1)}
            disabled={loading}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>
            Actualiser
          </button>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
              <p className="mt-2 text-muted">Chargement des données...</p>
            </div>
          ) : databases.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-database-x fs-1 text-muted"></i>
              <p className="mt-2 text-muted">Aucune base offline disponible</p>
              <p className="text-muted small">
                Utilisez le formulaire ci-dessus pour générer une base offline
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Classe</th>
                    <th>Fichier</th>
                    <th>Version</th>
                    <th>Taille</th>
                    <th>Généré le</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {databases.map((db, index) => (
                    <tr key={index}>
                      <td>
                        <span className="fw-semibold">{db.classe_label || "-"}</span>
                        {db.classe_slug && (
                          <small className="d-block text-muted">{db.classe_slug}</small>
                        )}
                      </td>
                      <td>
                        <code className="small">{db.filename}</code>
                      </td>
                      <td>
                        {db.version ? (
                          <span className="badge bg-secondary">{db.version}</span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>{db.size_formatted}</td>
                      <td>{formatDate(db.generated_at)}</td>
                      <td className="text-center">
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-success"
                            onClick={() => handleDownload(db.filename)}
                            title="Télécharger"
                          >
                            <i className="bi bi-download"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDelete(db.filename)}
                            title="Supprimer"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OfflineBackup;
